<template>
  <ChannelHeroSkeleton v-if="isLoading" v-bind="$attrs" />

  <div
    v-else
    v-bind="$attrs"
    id="home"
    class="min-h-[260px] large-desktop:min-h-[300px] p-2 flex flex-col relative bg-transparent"
    data-testid="channel-hero"
  >
    <div
      class="inset-0 absolute bg-no-repeat bg-cover bg-center mask-bottom"
      :style="{
        'background-image': `url('${themeStore.currentThemeData?.banner ?? (channelStore.channel?.banner as string)}')`,
      }"
    ></div>

    <div
      class="flex-1 flex flex-col justify-between overflow-hidden items-stretch max-w-full pb-0 px-3 w-full h-full"
    >
      <div class="flex flex-1 justify-end items-end relative flex-wrap">
        <div class="flex-1 self-end flex flex-col items-start gap-2">
          <div class="avatar">
            <div class="size-16 rounded-[8px]">
              <img
                class="!object-contain"
                :src="heroLogo"
                :alt="
                  channelStore.channel?.name
                    ? `${channelStore.channel.name} logo`
                    : 'Channel logo'
                "
              />
            </div>
          </div>

          <h1 class="text-lg text-text font-serif uppercase break-good">
            {{
              selectedCommunity?.name ??
              channelStore.channel?.description ??
              channelStore.channel?.name
            }}
          </h1>

          <!-- TODO: Leave a comment for design token-->
          <!--
            Under enable_new_channel_layout the member avatars + count relocate
            to the top bar (channel-top-bar.vue, which owns the URQL query); the hero
            members button is therefore hidden so the top bar is the sole owner.
          -->
          <ButtonV2
            v-if="!newChannelLayout"
            variant="subtle"
            size="md"
            class="space-x-1 text-sm text-text align-middle bg-fill-brand-secondary"
            data-testid="channel-members-button"
            @click="onMemberListClick"
          >
            <span class="inline-flex items-center -space-x-2.5 relative">
              <Avatar
                v-for="(entry, index) in leaderboardEntries"
                :key="entry.user.id"
                :user="{
                  username: entry.user.username,
                  avatar: entry.user.avatar,
                  id: entry.user.id,
                }"
                size="sm"
                class="rounded-full border-2 border-border-brand"
                :style="{
                  'z-index': leaderboardEntries.length - index,
                }"
              />
            </span>

            <span>{{
              formatCompactNumber(
                channelStore.channel?.members_count ?? 0,
                userStore.userLocale,
              )
            }}</span>

            <span>{{ $t("channel.titles.members") }}</span>
          </ButtonV2>
        </div>

        <div
          v-if="userStore.isAuthenticated"
          class="shrink-0 hidden large-desktop:block"
        >
          <ButtonV2
            variant="subtle"
            size="md"
            class="uppercase font-serif"
            @click="onShare"
          >
            <PointScoreBadge
              v-if="!channelStore.isLeaderboardDisabled"
              coins="1000"
              class="size-24u mr-2"
            />
            {{ $t("channel.buttons.inviteFriends") }}
          </ButtonV2>
        </div>

        <ChannelAdminDropdown
          v-if="
            userStore.isAuthenticated && channelStore.permissions.manageChannel
          "
          class="ml-2"
          @change-cover-image="onChangeCoverImage"
          @change-logo="onChangeLogo"
          @modify-color-theme="onModifyColorTheme"
          @show-community-management="onShowCommunityManagement"
        />
      </div>
    </div>
  </div>

  <div
    v-if="userStore.isAuthenticated && !isLoading"
    class="px-3 mt-4 large-desktop:hidden"
  >
    <ButtonV2
      variant="subtle"
      size="md"
      class="uppercase font-serif w-full py-3"
      @click="onShare"
    >
      <span
        class="relative inline-flex items-center justify-center z-10 self-start aspect-square h-[22px] rounded-full text-center font-serif text-text text-[8px] bg-fill-gold mr-2"
      >
        1000
      </span>

      {{ $t("channel.buttons.inviteFriends") }}
    </ButtonV2>
  </div>

  <ModifyColorThemeModal v-model:is-open="showModifyColorThemeModal" />
  <CommunityManagementModal v-model:is-open="showCommunityManagementModal" />
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import { useFileDialog } from "@vueuse/core";
import { useAppManagedUpload } from "@/composables/use-app-managed-upload";
import { GetChannelDocument } from "@/generated/graphql";
import { useAwaitAction } from "@/composables/use-await-action";

const ChannelHeroMembersDocument = gql(/* GraphQL */ `
  query channelHeroMembers($channel_id: String!, $community_id: String) {
    channelLeaderboard(
      channel_id: $channel_id
      highlights: false
      community_id: $community_id
      page: 0
      limit: 3
    ) {
      ... on Leaderboard {
        ranking {
          user {
            username
            avatar
            id
          }
        }
      }
    }
  }
`);

const UpdateCommunityLogoDocument = gql(/* GraphQL */ `
  mutation UpdateCommunityLogo($community_id: String!, $logo: String!) {
    updateCommunityLogo(community_id: $community_id, logo: $logo) {
      id
    }
  }
`);

const UpdateCommunityBannerDocument = gql(/* GraphQL */ `
  mutation UpdateCommunityBanner($community_id: String!, $banner: String!) {
    updateCommunityBanner(community_id: $community_id, banner: $banner) {
      id
    }
  }
`);

const UpdateChannelLogoDocument = gql(/* GraphQL */ `
  mutation UpdateChannelLogo($channel_id: String!, $logo: String!) {
    updateChannelLogo(channel_id: $channel_id, logo: $logo) {
      id
    }
  }
`);

const UpdateChannelBannerDocument = gql(/* GraphQL */ `
  mutation UpdateChannelBanner($channel_id: String!, $banner: String!) {
    updateChannelBanner(channel_id: $channel_id, banner: $banner) {
      id
    }
  }
`);
</script>

<script setup lang="ts">
import { useChannelStore } from "@/stores/channel.store";

import { formatCompactNumber } from "@/utils/format";
import { useUserStore } from "@/stores/user.store";
import { useTrackingStore } from "@/stores/tracking.store";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import ButtonV2 from "../ui/button/button-v2.vue";
import PointScoreBadge from "./contests/point-score-badge.vue";
import { useShareReferral } from "@/composables/use-share-referral";
import { computed, defineAsyncComponent, ref } from "vue";
import { AppThemeKind, useThemeStore } from "@/stores/theme.store";
import { useMutation, useQuery } from "@vue/apollo-composable";
import { useMutation as useUrqlMutation } from "@urql/vue";
import Avatar from "../me/avatar.vue";
import ChannelAdminDropdown from "./admin/channel-admin-dropdown.vue";

import ChannelHeroSkeleton from "@/components/ui/skeleton/channel-hero-skeleton.vue";
import { useFeatureFlags } from "@/composables/use-feature-flags";

// Lazy load
const ModifyColorThemeModal = defineAsyncComponent(
  () => import("./admin/modify-color-theme-modal.vue"),
);
const CommunityManagementModal = defineAsyncComponent(
  () => import("./admin/community-management-modal.vue"),
);

const userStore = useUserStore();
const channelStore = useChannelStore();
const trackingService = useTrackingStore();
const themeStore = useThemeStore();

const { getBoolean } = useFeatureFlags();
const newChannelLayout = getBoolean("enable_new_channel_layout");

const showModifyColorThemeModal = ref(false);
const showCommunityManagementModal = ref(false);

const showMemberList = defineModel<boolean>("showMemberList", {
  required: true,
});

const selectedCommunity = computed(() => {
  if (themeStore.currentTheme?.kind !== AppThemeKind.community) return null;

  return (
    channelStore.channel?.channel_communities.find(
      (c) => themeStore.currentTheme?.id === c.id,
    ) ?? null
  );
});

const { result: channelLeaderboardResult } = useQuery(
  ChannelHeroMembersDocument,
  computed(() => ({
    channel_id: channelStore.channelSlug!,
    community_id: selectedCommunity.value?.id,
  })),
  // Under enable_new_channel_layout the member avatars relocate to the top bar
  // (channel-top-bar.vue), which owns the (URQL) query. Apollo and URQL are
  // separate clients and do NOT dedupe each other, so disable this Apollo query
  // in the new layout to avoid a redundant network request. The hero's members
  // button is also hidden (v-if="!newChannelLayout") in that mode.
  {
    enabled: computed(
      () => !!channelStore.channelSlug && !newChannelLayout.value,
    ),
  },
);

const { mutate: updateCommunityLogo, loading: updateLogoLoading } = useMutation(
  UpdateCommunityLogoDocument,
  {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetChannelDocument,
        variables: {
          channel_id: channelStore.channelSlug!,
        },
      },
    ],
  },
);
const { mutate: updateCommunityBanner } = useMutation(
  UpdateCommunityBannerDocument,
);

const { mutate: updateChannelBanner } = useMutation(
  UpdateChannelBannerDocument,
);
const {
  executeMutation: updateChannelLogo,
  fetching: updateChannelLogoLoading,
} = useUrqlMutation(UpdateChannelLogoDocument);

const { open: openLogoDialog, onChange: onLogoUpload } = useFileDialog({
  accept: "image/*",
  directory: false,
});

const { open: openCoverImageDialog, onChange: onCoverImageUpload } =
  useFileDialog({
    accept: "image/*",
    directory: false,
  });

const { uploadContent: uploadLogo, loading: logoLoading } =
  useAppManagedUpload();
const { uploadContent: uploadBanner, loading: bannerLoading } =
  useAppManagedUpload();

onLogoUpload(async (files) => {
  if (!files || !files.length) return;

  const file = files[0] as File;

  if (!file) return;

  const url = await uploadLogo(file, "community-logos");

  if (selectedCommunity.value) {
    await updateCommunityLogo({
      community_id: selectedCommunity.value!.id,
      logo: url,
    });
  } else {
    await updateChannelLogo({
      channel_id: channelStore.channel?.id!,
      logo: url,
    });
  }
});

const {
  handler: updateCommunityBannerHandler,
  loading: updateCommunityBannerLoading,
} = useAwaitAction(async (banner: string) => {
  await updateCommunityBanner({
    community_id: selectedCommunity.value!.id,
    banner: banner,
  });

  await themeStore.refetchTheme();
});

const {
  handler: updateChannelBannerHandler,
  loading: _updateChannelBannerLoading,
} = useAwaitAction(async (banner: string) => {
  await updateChannelBanner({
    channel_id: channelStore.channel?.id!,
    banner: banner,
  });

  await themeStore.refetchTheme();
});

onCoverImageUpload(async (files) => {
  if (!files || !files.length) return;

  const file = files[0] as File;

  if (!file) return;
  const url = await uploadBanner(file, "community-banners");
  if (selectedCommunity.value) {
    await updateCommunityBannerHandler(url);
  } else {
    await updateChannelBannerHandler(url);
  }
});

const leaderboardEntries = computed(() =>
  channelLeaderboardResult?.value?.channelLeaderboard.__typename ===
  "Leaderboard"
    ? channelLeaderboardResult?.value?.channelLeaderboard.ranking
    : [],
);

const heroLogo = computed(
  () => selectedCommunity.value?.image ?? channelStore.channel?.logo ?? "",
);

function onMemberListClick() {
  trackingService.track({
    event: TrackEventEnum.MemberListClicked,
    category: EventCategory.Channels,
  });

  if (channelStore.isLeaderboardDisabled) return;
  showMemberList.value = true;
}

const onShare = useShareReferral({
  openGraphObject: computed(() => ({
    title: channelStore.channel?.description ?? channelStore.channelSlug!,
  })),
  referralObject: computed(() => ({
    title: `${channelStore.channel?.description} - FanFest`,
    text: `Join ${channelStore.channel?.description} on FanFest and earn rewards!`,
    payload: {
      community_id: selectedCommunity.value?.id,
    },
  })),
});

function onChangeCoverImage() {
  openCoverImageDialog();
}

function onChangeLogo() {
  openLogoDialog();
}

function onModifyColorTheme() {
  showModifyColorThemeModal.value = true;
}

function onShowCommunityManagement() {
  showCommunityManagementModal.value = true;
}

const isLoading = computed(
  () =>
    themeStore.loading ||
    bannerLoading.value ||
    updateCommunityBannerLoading.value ||
    logoLoading.value ||
    updateLogoLoading.value ||
    updateChannelLogoLoading.value ||
    !channelStore.channel,
);
</script>

<style scoped>
.mask-bottom {
  mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
}

.mask-top-and-bottom {
  mask-image: linear-gradient(to bottom, #00000030, #000 35%, transparent 100%);
}
</style>
